// tenant/UserInfoContext.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { Auth, API } from 'aws-amplify';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { Driver } from './model/types';

type Address = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  building: string;
  parking: string;
};

type Plan = {
  minStay: string;
  typeOfPlan: string;
  size: string;
  price: string;
};

type TenantUserInfoContextType = {
  // basic info
  email: string;
  name: string;
  phone: string;

  // address
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  building: string;
  parking: string;

  // facility / plan
  facilityID: string;
  facilityName: string;
  facilityAddress: string;
  minStay: string;
  typeOfPlan: string;
  size: string;
  price: string;
  flatFee?: number | null;
  hourlyFee?: number | null;
  unitID?: string | null;

  // data lists
  dataSourceStorage: any[];
  dataSourceHome: any[];
  dataSourcePastOrders: any[];
  dataSourceNewOrders: any[];
  appointmentsList: any[];
  drivers: Driver[];

  // loading flags
  loading: boolean;

  // actions
  fetchData: () => Promise<void>;
  updateUserName: (email: string, updatedName: string) => Promise<void>;
  updateUserPhone: (email: string, updatedPhone: string) => Promise<void>;
  updateUserAddress: (email: string, updatedAddress: Address) => Promise<void>;
};

const TenantUserInfoContext = createContext<TenantUserInfoContextType | undefined>(
  undefined
);

export const TenantUserInfoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<Omit<TenantUserInfoContextType, 'fetchData' | 'updateUserName' | 'updateUserPhone' | 'updateUserAddress'>>({
    email: '',
    name: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    specialInstructions: '',
    building: '',
    parking: '',
    facilityID: '',
    facilityName: '',
    facilityAddress: '',
    minStay: '',
    typeOfPlan: '',
    size: '',
    price: '',
    flatFee: undefined,
    hourlyFee: undefined,
    unitID: undefined,
    dataSourceStorage: [],
    dataSourceHome: [],
    dataSourcePastOrders: [],
    dataSourceNewOrders: [],
    appointmentsList: [],
    drivers: [],
    loading: false,
  });

  const fetchData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));

      const currentUserInfo = await Auth.currentUserInfo();
      const userEmail = currentUserInfo.attributes['email'];

      // tenant info
      const tenantInfo: any = await API.graphql({
        query: queries.getTenant,
        variables: { id: userEmail },
      });

      const tenant = tenantInfo?.data?.getTenant;
      if (!tenant) {
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      const tenantFacilityID = tenant.facilityID;
      const userName = tenant.name;
      const userPhone = tenant.phone;

      const addr = tenant.address || {};
      const plan = tenant.plan || {};

      const userAddressBuilding = addr.building || '';
      const userAddressCity = addr.city || '';
      const userAddressParking = addr.parking || '';
      const userAddressState = addr.state || '';
      const userAddressAddressLine1 = addr.addressLine1 || '';
      const userAddressAddressLine2 = addr.addressLine2 || '';
      const userAddressZip = addr.zip || '';
      const userAddressSpecialInstructions = addr.specialInstructions || '';

      const userPlanMinStay = plan.minStay || '';
      const userPlanType = plan.typeOfPlan || '';
      const userPlanSize = plan.size || '';
      const userPlanPrice = plan.price || '';

      const unitID = tenant.unitID ?? null;

      // boxes
      const tenantBoxes: any = await API.graphql({
        query: queries.boxesByTenant,
        variables: { tenantID: userEmail },
      });

      const boxesItems = tenantBoxes?.data?.boxesByTenant?.items ?? [];
      const boxesInStorage = boxesItems.filter((item: any) => item.status === 'IN_STORAGE');
      const boxesReturned = boxesItems.filter((item: any) => item.status === 'RETURNED');

      // orders
      const tenantOrders: any = await API.graphql({
        query: queries.ordersByTenant,
        variables: { tenantID: userEmail },
      });

      const ordersItems = tenantOrders?.data?.ordersByTenant?.items ?? [];
      const pastOrders = ordersItems.filter((item: any) => item.status === 'COMPLETED');
      const newOrders = ordersItems.filter(
        (item: any) => item.status === 'INCOMPLETE' || item.status === 'PENDING'
      );

      // appointments
      const facilityAppointments: any = await API.graphql({
        query: queries.appointmentsByFacility,
        variables: { facilityID: tenantFacilityID },
      });
      const appointmentsData = facilityAppointments?.data?.appointmentsByFacility?.items ?? [];

      // facility details
      const facilityDetails: any = await API.graphql({
        query: queries.getFacility,
        variables: { id: tenantFacilityID },
      });

      const facility = facilityDetails?.data?.getFacility;
      const storageFacilityName = facility?.name || '';
      const storageFacilityAddress = facility?.address || '';
      const storageFlatFee = facility?.movingCosts?.flatFee ?? null;
      const storageHourlyFee = facility?.movingCosts?.hourlyFee ?? null;

      // drivers for this facility
      const driversResult: any = await API.graphql({
        query: queries.driversByFacility,
        variables: { facilityID: tenantFacilityID },
      });
      const facilityDrivers: Driver[] = driversResult?.data?.driversByFacility?.items ?? [];

      setState(prev => ({
        ...prev,
        email: userEmail,
        name: userName,
        phone: userPhone,
        addressLine1: userAddressAddressLine1,
        addressLine2: userAddressAddressLine2,
        city: userAddressCity,
        state: userAddressState,
        zip: userAddressZip,
        specialInstructions: userAddressSpecialInstructions,
        building: userAddressBuilding,
        parking: userAddressParking,
        facilityID: tenantFacilityID,
        facilityName: storageFacilityName,
        facilityAddress: storageFacilityAddress,
        minStay: userPlanMinStay,
        typeOfPlan: userPlanType,
        size: userPlanSize,
        price: userPlanPrice,
        unitID,
        dataSourceHome: boxesReturned,
        dataSourceStorage: boxesInStorage,
        dataSourcePastOrders: pastOrders,
        dataSourceNewOrders: newOrders,
        appointmentsList: appointmentsData,
        drivers: facilityDrivers,
        flatFee: storageFlatFee,
        hourlyFee: storageHourlyFee,
        loading: false,
      }));
    } catch (error) {
      console.error('Error in TenantUserInfo fetchData:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const updateUserName = useCallback(async (userEmail: string, updatedName: string) => {
    const tenantDetails = {
      id: userEmail,
      name: updatedName,
    };
    await API.graphql({
      query: mutations.updateTenant,
      variables: { input: tenantDetails },
    });
    setState(prev => ({ ...prev, name: updatedName }));
  }, []);

  const updateUserPhone = useCallback(async (userEmail: string, updatedPhone: string) => {
    const tenantDetails = {
      id: userEmail,
      phone: updatedPhone,
    };
    await API.graphql({
      query: mutations.updateTenant,
      variables: { input: tenantDetails },
    });
    setState(prev => ({ ...prev, phone: updatedPhone }));
  }, []);

  const updateUserAddress = useCallback(async (userEmail: string, updatedAddress: Address) => {
    const tenantDetails = {
      id: userEmail,
      address: updatedAddress,
    };
    await API.graphql({
      query: mutations.updateTenant,
      variables: { input: tenantDetails },
    });
    setState(prev => ({
      ...prev,
      addressLine1: updatedAddress.addressLine1,
      addressLine2: updatedAddress.addressLine2,
      city: updatedAddress.city,
      state: updatedAddress.state,
      zip: updatedAddress.zip,
      specialInstructions: updatedAddress.specialInstructions,
      building: updatedAddress.building,
      parking: updatedAddress.parking,
    }));
  }, []);

  const value: TenantUserInfoContextType = {
    ...state,
    fetchData,
    updateUserName,
    updateUserPhone,
    updateUserAddress,
  };

  return (
    <TenantUserInfoContext.Provider value={value}>
      {children}
    </TenantUserInfoContext.Provider>
  );
};

export const useTenantUserInfo = (): TenantUserInfoContextType => {
  const ctx = useContext(TenantUserInfoContext);
  if (!ctx) throw new Error('useTenantUserInfo must be used within TenantUserInfoProvider');
  return ctx;
};
